import { prepareAssertions } from '~/packages/general/variable/prepareAssertions.util'
import { Variable } from '~/packages/general/variable/variables.typing'
import { healthVariableBaseRelevance } from '~/packages/general/variable/healthVariableRelevance.util'
import { defineAction } from '~/prix'
import expression from '~/prix/types/expression'
import { analysis } from '~/prix/types/presets/analysis'
import { assertions } from '~/prix/types/presets/assertions'
import { select } from '~/prix/types/presets/select'
import { spaceForPoint } from '~/prix/types/presets/space'
import { timeForCurrentOnly } from '~/prix/types/presets/time'
import { variable } from '~/prix/types/presets/variable'
import { getZodSchemaInfo } from '~/prix/types/zodType'
import { z } from 'zod'
import citizenHealthStatsAction from './citizenHealthStats.action.json' with { type: 'json' }
import { citizenHealthProfileShape } from '../../citizenHealthProfile/stats/citizenHealthProfileStats.definition'
import { citizenLocationShape, citizenShapeBase } from '../../citizen/stats/citizenShape'

export const citizenHealthShape = z.object({
  ...citizenShapeBase.shape,
  ...citizenLocationShape.shape,
  ...citizenHealthProfileShape.shape,
})

export const citizenHealthExpression = expression(
  citizenHealthShape.pick({
    sex: true,
    age: true,
    ageDecimal: true,
    ageRange: true,
    deceased: true,
    isResponsibleFamilyMember: true,
    hasDiabetes: true,
    hasHypertension: true,
    hadStroke: true,
    hadHeartAttack: true,
    hasHeartDisease: true,
    hasRespiratoryDisease: true,
    hadKidneyDisease: true,
    hasLeprosy: true,
    hasTuberculosis: true,
    hadCancer: true,
    hasMedicalProfessionalDiagnosisOfMentalConditions: true,
    hasAnyDisability: true,
    isPregnant: true,
    isBedridden: true,
    isDomiciled: true,
    smoker: true,
    consumesAlcoholicBeverages: true,
    takeDrugs: true,
    currentWeightStatus: true,
    eatHowManyTimesDay: true,
    skinColorOrRace: true,
    nationality: true,
    educationLevel: true,
    employmentStatus: true,
    sexualOrientation: true,
    kinshipWithFamilyResponsible: true,
    unknownMother: true,
    unknownFather: true,
    attendsSchoolOrDaycare: true,
    memberOfTraditionalGroupsOrCommunity: true,
    takePartInCommunityGroups: true,
    attendsAlternativeHealer: true,
    usesMedicinalPlants: true,
    makesUseOfOtherIntegrativeAndComplementaryPractices: true,
    homeless: true,
    homelessForHowManyTime: true,
    receivesBenefit: true,
    hasPrivateHealthInsurance: true,
    duringTheLastThreeMonthsHadFoodShortageWithoutFinancialMeansToGetMore: true,
    duringTheLastThreeMonthsAteOnlyAvailableFoodBecauseOfLackOfMoney: true,
    moved: true,
    childZeroToNineYearsOld: true,
    duringTheLast6MonthsHadAnyHospitalInternment: true,
    haveAnyKindOfFamilyReference: true,
    assistedByAnyInstitution: true,
    visitFamilyMembersOften: true,
    hasAccessToPersonalHygiene: true,
    refusedToRegister: true,
    wishToInformSexualOrientation: true,
    wishToInformGenderIdentity: true,
  }).shape,
)

export const citizenHealthVariable: Variable = {
  key: 'citizenHealth',
  actionDefinitionKey: citizenHealthStatsAction.key,
  title: 'Cadastro individual',
  categoryKey: 'healthIndividualProfile',
  unitSingular: 'cadastro individual',
  unitPlural: 'cadastros individuais',
  grammaticalGender: 'male',
  timeType: 'currentOnly',
  shape: getZodSchemaInfo(citizenHealthShape),
  filterableBy: getZodSchemaInfo(
    citizenHealthShape.pick({
      sex: true,
      ageRange: true,
      deceased: true,
      isResponsibleFamilyMember: true,
      hasPrivateHealthInsurance: true,
      memberOfTraditionalGroupsOrCommunity: true,
      hasAnyDisability: true,
      isPregnant: true,
      smoker: true,
      consumesAlcoholicBeverages: true,
      hasHypertension: true,
      hasDiabetes: true,
      hadStroke: true,
      hadHeartAttack: true,
      hasHeartDisease: true,
      hadKidneyDisease: true,
      hasRespiratoryDisease: true,
      hasLeprosy: true,
      hasTuberculosis: true,
      hadCancer: true,
      hasMedicalProfessionalDiagnosisOfMentalConditions: true,
      isBedridden: true,
      isDomiciled: true,
      homeless: true,
      receivesBenefit: true,
      unknownFather: true,
      unknownMother: true,
      attendsSchoolOrDaycare: true,
      takePartInCommunityGroups: true,
      skinColorOrRace: true,
      nationality: true,
      kinshipWithFamilyResponsible: true,
      educationLevel: true,
      employmentStatus: true,
      sexualOrientation: true,
      currentWeightStatus: true,
      homelessForHowManyTime: true,
      eatHowManyTimesDay: true,
      wishToInformSexualOrientation: true,
      wishToInformGenderIdentity: true,
      duringTheLastThreeMonthsHadFoodShortageWithoutFinancialMeansToGetMore: true,
      duringTheLastThreeMonthsAteOnlyAvailableFoodBecauseOfLackOfMoney: true,
      moved: true,
      attendsAlternativeHealer: true,
      childZeroToNineYearsOld: true,
      duringTheLast6MonthsHadAnyHospitalInternment: true,
      usesMedicinalPlants: true,
      makesUseOfOtherIntegrativeAndComplementaryPractices: true,
      haveAnyKindOfFamilyReference: true,
      assistedByAnyInstitution: true,
      visitFamilyMembersOften: true,
      hasAccessToPersonalHygiene: true,
      refusedToRegister: true,
      takeDrugs: true,
    }),
  ),
  betterDensity: 'more',
  relevance: healthVariableBaseRelevance.citizenHealth,
  basicSelect: ['name', 'socialName', 'birthDate', 'sex'],
  detailedSelect: [
    'cns',
    'cpf',
    'phoneNumber',
    'latitude',
    'longitude',
    'isResponsibleFamilyMember',
    'mothersName',
    'fathersName',
    'email',
    'angleFromStreet',
    'streetName',
    'streetNumber',
    'complement',
    'referencePoint',
    'postalCode',
    'neighborhood',
    'municipality',
    'skinColorOrRace',
    'nationality',
    'countryOfBirth',
    'birthplaceMunicipality',
    'birthplaceState',
    'occupation',
    'hasPrivateHealthInsurance',
    'memberOfTraditionalGroupsOrCommunity',
    'hasAnyDisability',
    'isPregnant',
    'currentWeightStatus',
    'smoker',
    'consumesAlcoholicBeverages',
    'takeDrugs',
    'hasHypertension',
    'hasDiabetes',
    'hadStroke',
    'hadHeartAttack',
    'hasHeartDisease',
    'whichHeartDisease',
    'hadKidneyDisease',
    'whichKidneyDisease',
    'hasRespiratoryDisease',
    'whichRespiratoryDisease',
    'hasLeprosy',
    'hasTuberculosis',
    'hadCancer',
    'hasMedicalProfessionalDiagnosisOfMentalConditions',
    'isBedridden',
    'isDomiciled',
    'homeless',
    'homelessForHowManyTime',
    'receivesBenefit',
    'hasPrivateHealthInsurance',
  ],
  assertions: prepareAssertions(citizenHealthExpression),
  timeSeries: {
    fitDays: 365 * 10,
    layers: [
      {
        layer: 'calendarSeasonality',
        type: 'monthOfYear',
        degree: 1,
        optimization: {
          minimumDays: 1095,
          lossFunction: 'recentWeightedMeanSquaredError',
        },
      },
      {
        layer: 'trend',
        degree: 2,
      },
      {
        layer: 'calendarSeasonality',
        type: 'dayOfWeek',
        degree: 1,
        optimization: {
          minimumDays: 60,
          lossFunction: 'recentWeightedMeanSquaredError',
        },
      },
      {
        layer: 'calendarSeasonality',
        type: 'holiday',
        degree: 1,
      },
      {
        layer: 'calendarSeasonality',
        type: 'dayOfMonth',
        degree: 1,
      },
      {
        layer: 'harmonicSeasonality',
        optimization: {
          minimumDays: 30,
          lossFunction: 'gradientMeanSquaredError',
        },
      },
      {
        layer: 'harmonicSeasonality',
        optimization: {
          minimumDays: 30,
          lossFunction: 'gradientMeanSquaredError',
        },
      },
    ],
  },
}

const citizenHealthStatsDefinition = defineAction({
  ...citizenHealthStatsAction,
  input: z.object({
    variable,
    highlight: citizenHealthExpression.optional(),
    filter: citizenHealthExpression.optional(),
    space: spaceForPoint().optional(),
    time: timeForCurrentOnly().optional(),
    select: select(citizenHealthShape),
    assertions,
    analysis,
  }),
  output: z.any(),
  cache: input => {
    if (input.filter || input.space?.delimitationType === 'custom') {
      return {}
    }
    return {
      back: {
        expirationMsForFallback: 1000 * 60 * 60 * 24 * 7,
        expirationMsForUpdate: 1000 * 60 * 60 * 2,
      },
      front: {
        expirationMsForFallback: 1000 * 60 * 60 * 24 * 7,
        expirationMsForUpdate: 1000 * 60 * 60 * 2,
      },
    }
  },
})

export default citizenHealthStatsDefinition
